import { IconButton, Paper } from "@mui/material";
import styled from "styled-components/macro";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Close } from "@mui/icons-material";
import { MACHEMETRY_URL } from "providers/MachemetryProvider";
import { useFocusRange, useFormatZoned } from "utils/customHooks/genericHooks";
import { useEffect, useMemo } from "react";
import { Measurement, SIGNALS } from "./signalValidationUtils";
import { FORMAT_DAY_TIME_WITH_SECONDS, FORMAT_DAY_TIME } from "utils/timeUtils";
import { startCase } from "lodash";
import { atom, useAtom } from "jotai";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { usePrevious } from "react-use";
import { useTimelineTooltipStore } from "../timelineTooltipStore";
import { useSelector } from "store/useSelector";

// keep signal data in global state to avoid re-fetching until focus range changes
const signalDataAtom = atom<{
  loading: boolean;
  error: any;
  data: { [key: string]: Measurement[] };
}>({
  loading: true,
  error: null,
  data: {},
});
type DataKey = keyof Measurement["v"]["current"];
type DataKeyFlattened =
  | DataKey
  | "ts"
  | "v"
  | "value"
  | "payload"
  | "machineState"
  | "reason"
  | "skuCode"
  | "notificationGroupId"
  | "callStatus"
  | "path";

const LINE_COLORS = ["#8884d8", "#82ca9d", "#ffc658"];

const LINES_BY_SIGNAL: {
  [SignalId in keyof typeof SIGNALS]: {
    dataKey: DataKeyFlattened;
    stroke: string;
    type?: "category" | "number";
    axisTitle?: string;
    yAxisId: "left" | "right";
    reversed?: boolean;
  }[];
} = {
  hubletTag_0: [
    {
      dataKey: "machineState",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "category",
      reversed: true,
    },
    {
      dataKey: "path",
      stroke: LINE_COLORS[1],
      yAxisId: "right",
      type: "category",
    },
    // {
    //   dataKey: "reason",
    //   stroke: LINE_COLORS[2],
    //   yAxisId: "right",
    //   type: "category",
    // },
  ],
  workflow_0: [
    {
      dataKey: "workflowState",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "category",
    },
    {
      dataKey: "tagId",
      stroke: LINE_COLORS[1],
      yAxisId: "right",
      type: "category",
    },
  ],
  netCount_0: [
    {
      dataKey: "v",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "number",
    },
  ],
  grossCount_0: [
    {
      dataKey: "v",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "number",
    },
  ],
  rejectCount_0: [
    {
      dataKey: "v",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "number",
    },
  ],
  opsku_0: [
    {
      dataKey: "skuCode",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "category",
    },
  ],
  plcTags_0: [
    {
      dataKey: "value",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "category",
    },
  ],
  call_0: [
    {
      dataKey: "callStatus",
      stroke: LINE_COLORS[0],
      yAxisId: "left",
      type: "category",
    },
    {
      dataKey: "notificationGroupId",
      stroke: LINE_COLORS[1],
      yAxisId: "right",
      type: "category",
    },
  ],
};

export function SignalValidationChart({
  activeSignal,
  activeUrl,
  machemetryId,
  handleClose,
  isViewingEntireShift,
}: {
  activeSignal: keyof typeof SIGNALS | "";
  activeUrl: string;
  machemetryId: string;
  handleClose: () => void;
  isViewingEntireShift: "segment" | "shift" | "all";
}) {
  const focusRange = useFocusRange();
  const visibleRange = useSelector(s => s.legacy.visibleRange);
  const signalId = `${machemetryId}_${activeSignal}`;
  const [{ selectedProdSegment }] = useTimelineTooltipStore();

  const [{ data: signalData, loading }, setState] = useAtom(signalDataAtom);

  const range =
    isViewingEntireShift === "all"
      ? visibleRange
      : isViewingEntireShift === "shift"
        ? focusRange
        : selectedProdSegment?.range;
  const prevRange = usePrevious(range);
  const didRangeChange =
    prevRange &&
    range &&
    (prevRange.ge !== range.ge || prevRange.lt !== range.lt);
  useEffect(() => {
    if (didRangeChange) {
      setState(p => ({ ...p, loading: true }));
    }
  }, [didRangeChange, setState]);

  useEffect(() => {
    // only fetch once per signal until the focusRange changes
    if (signalData[signalId] && !didRangeChange) {
      return;
    }
    const variablesString = activeUrl.split("?")[1];

    const variablesSplit: {
      s?: string;
    } = variablesString.split("&").reduce((acc, curr) => {
      const [key, value] = curr.split("=");
      return { ...acc, [key]: value };
    }, {});

    const variables = {
      ...variablesSplit,
      s: variablesSplit.s ?? "",
      // by default, show the whole shift focus range in the chart
      gte: range?.ge && range?.ge / 1000,
      lt: range?.lt && range?.lt / 1000,
    };
    const idToken = window.localStorage.getItem("id_token");

    fetch(
      `${MACHEMETRY_URL}v2/measurements?s=${variables.s}&gte=${variables.gte}&lt=${variables.lt}`,
      {
        method: "GET",
        headers: {
          authorization: idToken ? `Bearer ${idToken}` : "no-token",
        },
      }
    )
      .then(response => response.json())
      .then(d => {
        console.log("fetched signal data", d);
        setState(p => ({ ...p, loading: false, data: { ...p.data, ...d } }));
      })
      .catch(error => {
        setState(p => ({ ...p, loading: false, error }));
      });
  }, [activeUrl, range, setState, signalData, signalId, didRangeChange]);

  const { dataArrayFiltered, xDomain } = useMemo(() => {
    const domain =
      range && range.ge
        ? [Number(range.ge), range.lt ? Number(range.lt) : Date.now()]
        : undefined;
    const dataArray = signalData[signalId];

    const dataArrayFlattened = (dataArray ?? []).map(({ ts, v }) => ({
      ts,
      ...v.current,
      ...v.payload,
      ...v,
      path: v?.payload?.path?.join(","),
      value: v?.location?.value,
    }));
    // filter to segment or shift depending on the toggle state
    const filtered = dataArrayFlattened.filter(({ ts }) => {
      const ms = ts * 1000;
      return domain?.[0] && domain?.[1]
        ? domain[0] <= ms && ms <= domain[1]
        : true;
    });
    return {
      dataArrayFiltered: filtered,
      xDomain: domain,
    };
  }, [signalData, signalId, range]);

  const formatZoned = useFormatZoned();
  const lines = LINES_BY_SIGNAL[activeSignal];
  const rightAxisLine = lines.find(line => line.yAxisId === "right");

  return (
    <SignalValidationChartStyles>
      <Paper className="chart-wrapper" elevation={4}>
        <IconButton className="close-button" onClick={handleClose}>
          <Close />
        </IconButton>
        <h1>{activeSignal}</h1>

        <div style={{ flexGrow: 1, width: "100%" }}>
          {loading && (
            <div
              style={{
                position: "absolute",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Spinner width={32} />
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={730}
              height={250}
              data={dataArrayFiltered}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <Legend verticalAlign="top" height={36} />
              <XAxis
                scale="time"
                dataKey={"ts" as DataKeyFlattened}
                domain={xDomain}
                tickFormatter={(ts: number) =>
                  formatZoned(new Date(ts * 1000), FORMAT_DAY_TIME)
                }
              />

              {lines[0] && (
                <YAxis
                  yAxisId="left"
                  label={{
                    value: lines[0].axisTitle ?? startCase(lines[0]?.dataKey),
                    angle: -90,
                    position: "insideLeft",
                  }}
                  reversed={lines[0].reversed}
                  dataKey={lines[0].dataKey}
                  stroke={lines[0].stroke}
                  type={lines[0].type ?? "category"}
                />
              )}

              {rightAxisLine && (
                <YAxis
                  yAxisId="right"
                  label={{
                    value:
                      rightAxisLine.axisTitle ??
                      startCase(rightAxisLine.dataKey),
                    angle: 90,
                    position: rightAxisLine.yAxisId,
                  }}
                  orientation={rightAxisLine.yAxisId}
                  dataKey={rightAxisLine.dataKey}
                  stroke={rightAxisLine.stroke}
                  type={"category"}
                />
              )}

              {lines[2] && (
                <YAxis
                  yAxisId="third"
                  label={{
                    value: lines[2].axisTitle ?? startCase(lines[2]?.dataKey),
                    angle: 90,
                    position: lines[2].yAxisId,
                  }}
                  orientation={lines[2].yAxisId}
                  reversed={lines[2].reversed}
                  dataKey={lines[2].dataKey}
                  stroke={lines[2].stroke}
                  type={lines[2].type ?? "category"}
                />
              )}

              <Tooltip
                labelFormatter={(value: number) =>
                  formatZoned(
                    new Date(value * 1000),
                    FORMAT_DAY_TIME_WITH_SECONDS
                  )
                }
              />

              {lines.map(({ dataKey, stroke, yAxisId }) => (
                <Line
                  yAxisId={yAxisId}
                  key={dataKey}
                  dataKey={dataKey}
                  stroke={stroke}
                  fill={stroke}
                  strokeWidth={1}
                  dot={true}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                  strokeDasharray="1 4"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Paper>
    </SignalValidationChartStyles>
  );
}

const WIDTH = Math.min(window.innerWidth - 32, 480);
const HEIGHT = Math.min(window.innerHeight - 32, (WIDTH * 2) / 3);

const SignalValidationChartStyles = styled.div`
  .MuiPaper-root {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  position: fixed;
  width: ${WIDTH}px;
  height: ${HEIGHT}px;
  top: calc(50vh - ${HEIGHT / 2}px);
  left: calc(50vw - ${WIDTH / 2}px);
  max-width: calc(100vw - 32px);
  z-index: 100000;
  .chart-wrapper {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
  }
  .close-button {
    cursor: pointer;
    z-index: 100;
    position: absolute;
    top: 0;
    right: 0;
  }
  .recharts-default-tooltip {
    transform: scale(0.75);
  }
  .recharts-cartesian-axis-tick-value {
    pointer-events: none;
  }
`;
